const SCROLL_DEBUG_SESSION_KEY = 'scrollDebug';
const SCROLL_DEBUG_QUERY = 'scrollDebug';

export type ScrollDebugPayload = Record<string, unknown>;

export const isScrollDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (process.env.NODE_ENV !== 'production') return true;
  if (window.location.search.includes(`${SCROLL_DEBUG_QUERY}=1`)) return true;
  try {
    return sessionStorage.getItem(SCROLL_DEBUG_SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

export const logScrollTrigger = (source: string, payload: ScrollDebugPayload = {}): void => {
  if (!isScrollDebugEnabled()) return;

  console.debug('[scroll-debug]', {
    source,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    scrollY: window.scrollY,
    documentHeight: document.documentElement.scrollHeight,
    bodyOffsetHeight: document.body?.offsetHeight ?? null,
    ...payload,
    timestamp: performance.now(),
  });
};

const parseScrollToArgs = (args: unknown[]): ScrollDebugPayload => {
  const [first, second] = args;
  if (typeof first === 'object' && first !== null) {
    const options = first as ScrollToOptions;
    return {
      top: options.top,
      left: options.left,
      behavior: options.behavior,
    };
  }
  return { top: first as number | undefined, left: second as number | undefined };
};

const getStackSnippet = (): string | undefined => new Error().stack?.split('\n').slice(2, 10).join('\n');

let patchesInstalled = false;

/** Patches window.scrollTo and Element.scrollIntoView to log all programmatic scrolls (incl. Next.js router). */
export const installScrollDebugPatches = (): void => {
  if (typeof window === 'undefined' || patchesInstalled || !isScrollDebugEnabled()) return;
  patchesInstalled = true;

  console.debug(
    '[scroll-debug] enabled — on review: sessionStorage.setItem("scrollDebug", "1") or add ?scrollDebug=1 to URL',
  );

  const originalScrollTo = window.scrollTo.bind(window);
  window.scrollTo = ((...args: unknown[]) => {
    logScrollTrigger('window.scrollTo (native patch)', {
      ...parseScrollToArgs(args),
      stack: getStackSnippet(),
    });
    return originalScrollTo(...(args as Parameters<typeof window.scrollTo>));
  }) as typeof window.scrollTo;

  const originalScrollIntoView = Element.prototype.scrollIntoView;
  Element.prototype.scrollIntoView = function scrollIntoViewWithDebug(
    this: Element,
    ...args: Parameters<Element['scrollIntoView']>
  ) {
    logScrollTrigger('Element.scrollIntoView (native patch)', {
      tagName: this.tagName,
      id: this.id || null,
      className: typeof this.className === 'string' ? this.className.slice(0, 120) : null,
      dataset: this instanceof HTMLElement ? { ...this.dataset } : null,
      args,
      stack: getStackSnippet(),
    });
    return originalScrollIntoView.apply(this, args);
  };
};
